<template>
    <div class="form enter-contact-data-form">
        <div class="full-name-container">
            <TextInput
                v-if="firstNameRequirement !== InputRequirementType.NotRequired"
                class="first-name-input"
                ref="firstNameComponent"
                v-model:value="firstName"
                :maxCharLength="60"
                :requirement-type="firstNameRequirement"
                :label="translations.general.firstName"
            />
            <TextInput
                v-if="lastNameRequirement !== InputRequirementType.NotRequired"
                class="last-name-input"
                ref="lastNameComponent"
                :maxCharLength="60"
                :requirement-type="lastNameRequirement"
                v-model:value="lastName"
                :label="translations.general.lastName"
            />
        </div>

        <PhoneNumberInput
            style="margin-bottom: 12px"
            v-model:requirement-type="phoneNumberRequirement"
            ref="phoneNumberComponent"
            v-if="phoneNumberRequirement !== InputRequirementType.NotRequired"
        />

        <EmailInput
            style="margin-bottom: 12px"
            v-model:requirement-type="emailRequirement"
            v-model:value="emailAddress"
            ref="emailComponent"
            v-if="showEmailField"
        />

        <VButton :class="{ loading: isLoading }" class="save-btn primary rounded" @click="save">{{
            trans.general.save
        }}</VButton>
    </div>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { InputRequirementType } from "@/components/elements/InputRequirementType";
    import { Factory } from "@/data/dao/Factory";
    import { CustomerDao } from "@/data/dao/service/CustomerDao";
    import { DiscountType, OrdererEmailRequirement, OrdererNameRequirement, OrdererPhoneNumberRequirement } from "@/data/model/Enums";
    import { useCartStore } from "@/data/stores/cart";
    import { useIdentityStore } from "@/data/stores/identity";
    import { trans } from "@/operator/localization/Trans";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, onMounted, ref } from "vue";

    import EmailInput from "@/components/elements/EmailInput.vue";
    import PhoneNumberInput from "@/components/elements/PhoneNumberInput.vue";
    import TextInput from "@/components/elements/TextInput.vue";
    import VButton from "@/components/elements/VButton.vue";
    import SelectFromEnumInput from "@/portal/components/elements/SelectFromEnumInput.vue";

    const cart = useCartStore(); // for getting current order service
    const identity = useIdentityStore();

    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);

    // define props
    const props = defineProps<{
        hide?: () => void;
        isInOperatorApp: boolean;
    }>();

    // define emits
    const emit = defineEmits(["onContactDataUpdated"]);

    const isLoading = ref<boolean>(false);
    const phoneNumberComponent = ref<InstanceType<typeof PhoneNumberInput> | undefined>();
    const firstNameComponent = ref<InstanceType<typeof TextInput> | undefined>();
    const lastNameComponent = ref<InstanceType<typeof TextInput> | undefined>();
    const emailComponent = ref<InstanceType<typeof EmailInput> | undefined>();

    const firstName = ref<string | undefined>(cart.currentCart?.contactDataEndUser?.firstName);
    const lastName = ref<string | undefined>(cart.currentCart?.contactDataEndUser?.lastName);
    const emailAddress = ref<string | undefined>(cart.currentCart?.contactDataEndUser?.email);
    const ordererRequirements = computed(() => {
        return cart.orderService?.useCase?.ordererRequirements;
    });

    const hasOperatorAccess = computed(() => {
        return identity.hasOperatorAccess;
    });

    const showEmailField = computed<boolean>(() => {
        if (hasOperatorAccess.value) {
            if (isRegisteredRequired.value) {
                return true;
            } else {
                return ordererRequirements.value!.isEmailRequirementEnabled;
            }
        } else {
            return ordererRequirements.value?.emailRequirement !== OrdererEmailRequirement.NotRequired;
        }
    });

    const firstNameRequirement = ref<InputRequirementType>(
        convertToInputRequirementType(ordererRequirements.value?.firstNameRequirement)
    );
    const lastNameRequirement = ref<InputRequirementType>(
        convertToInputRequirementType(ordererRequirements.value?.lastNameRequirement)
    );
    const emailRequirement = ref<InputRequirementType>(
        convertEmailToInputRequirementType(
            ordererRequirements.value?.emailRequirement,
            ordererRequirements.value?.isRegisteredRequired ?? false
        )
    );

    const phoneNumberRequirement = ref<InputRequirementType>(
        convertPhoneToInputRequirementType(ordererRequirements.value?.phoneNumberRequirement)
    );

    const isRegisteredRequired = computed<boolean>(
        () => ordererRequirements.value?.isRegisteredRequirementEnabled === true
    );

    function convertToInputRequirementType(
        ordererNameRequirement: OrdererNameRequirement | undefined
    ): InputRequirementType {
        switch (ordererNameRequirement) {
            case OrdererNameRequirement.NotRequired:
                return InputRequirementType.NotRequired;
            case OrdererNameRequirement.Optional:
                return InputRequirementType.Optional;
            case OrdererNameRequirement.Required:
                return InputRequirementType.Required;
            case null:
                throw new Error("No orderer requirement found");
        }

        throw new Error("Unknown orderer requirement");
    }

    function convertEmailToInputRequirementType(
        ordererEmailRequirement: OrdererEmailRequirement | undefined,
        isRegisteredRequired: boolean
    ): InputRequirementType {
        if (isRegisteredRequired && (identity.isAdmin || identity.isWaiterInCurrentLocation)) {
            return InputRequirementType.Required;
        }

        switch (ordererEmailRequirement) {
            case OrdererEmailRequirement.NotRequired:
                return InputRequirementType.NotRequired;
            case OrdererEmailRequirement.Optional:
                return InputRequirementType.Optional;
            case OrdererEmailRequirement.Required:
                return InputRequirementType.Required;
            case OrdererEmailRequirement.ConfirmedBlockOrder:
                if (identity.isAdmin || identity.isWaiterInCurrentLocation) {
                    return InputRequirementType.Required; // Show the field to provide registered end user email
                } else {
                    return InputRequirementType.NotRequired;
                }
            case null:
                throw new Error("No orderer requirement found");
        }

        throw new Error("Unknown orderer requirement");
    }
    function convertPhoneToInputRequirementType(
        ordererNameRequirement: OrdererPhoneNumberRequirement | undefined
    ): InputRequirementType {
        switch (ordererNameRequirement) {
            case OrdererPhoneNumberRequirement.NotRequired:
                return InputRequirementType.NotRequired;
            case OrdererPhoneNumberRequirement.Optional:
                return InputRequirementType.Optional;
            case OrdererPhoneNumberRequirement.Required:
            case OrdererPhoneNumberRequirement.ConfirmedBlockOrder:
                return InputRequirementType.Required;
            case null:
                throw new Error("No orderer requirement found");
        }

        throw new Error("Unknown orderer requirement");
    }

    onMounted(() => {
        phoneNumberComponent.value?.setValue(cart.currentCart?.contactDataEndUser?.phoneNumber);
    });

    async function save() {
        // Reset component states
        phoneNumberComponent.value?.setError(false);
        firstNameComponent.value?.setError(false);
        lastNameComponent.value?.setError(false);
        emailComponent.value?.setError(false);

        const phoneNumberResult = phoneNumberComponent.value?.getResult();
        const currentFirstName = firstName.value;
        const currentLastName = lastName.value;
        const currentEmailAddress = emailAddress.value;

        let isDataValid = true;

        if (firstNameComponent.value != null && !firstNameComponent.value!.isValid()) {
            isDataValid = false;
            firstNameComponent.value!.setError(true);
        }

        if (lastNameComponent.value != null && !lastNameComponent.value!.isValid()) {
            isDataValid = false;
            lastNameComponent.value!.setError(true);
        }

        if (phoneNumberComponent.value != null) {
            if (!phoneNumberResult!.isValid) {
                isDataValid = false;
                phoneNumberComponent.value!.setError(true);
            }
        }

        if (emailComponent.value != null && !emailComponent.value!.isValid()) {
            isDataValid = false;
            emailComponent.value!.setError(true);
        }

        if (isDataValid) {
            try {
                isLoading.value = true;

                let contactDataEndUser = await customerDao.updateOrCreateOrderCustomerContactData({
                    firstName: currentFirstName,
                    lastName: currentLastName,
                    email: emailComponent.value == undefined ? undefined : currentEmailAddress,
                    phoneNumber: phoneNumberComponent.value == undefined ? undefined : phoneNumberResult?.phoneNumber,
                    phoneCountryCode: phoneNumberComponent.value == undefined ? undefined : phoneNumberResult?.dialCode,
                    indirectIdentityId: cart.currentCart?.contactDataEndUser?.identityId,
                });

                cart.setContactData(
                    contactDataEndUser.firstName,
                    contactDataEndUser.lastName,
                    contactDataEndUser.email,
                    contactDataEndUser.phoneNumber,
                    contactDataEndUser.identityId
                );

                if (!identity.hasOperatorAccess) {
                    identity.onIdentityChanged({ isLogout: false, profile: contactDataEndUser });
                }

                emit("onContactDataUpdated");

                if (props.hide) {
                    props.hide();
                }
            } catch (e) {
                logError(e);
                Popup.show({
                    message: trans.general.operationFailed,
                });
            } finally {
                isLoading.value = false;
            }
        }
    }
</script>

<style scoped lang="scss">
    .enter-contact-data-form {
        > .full-name-container {
            display: flex;
            width: 100%;
            margin-bottom: 12px;

            .first-name-input {
                flex-grow: 1;
                margin-right: 12px;
            }

            .last-name-input {
                flex-grow: 1;
                margin-left: 12px;
            }
        }

        > .save-btn {
            height: 46px;
            font-size: 16px;
            font-weight: 300;
            width: 100%;
            border-radius: 23px !important;
            margin-bottom: 14px;
        }
    }
</style>
